import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Network, CreditCard, AlertTriangle, CheckCircle, Clock, ChevronRight, Plus, Printer } from 'lucide-react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { AssociationOfficerPanel } from './AssociationOfficerPanel'
import { PortalPageHeader } from './ui/PortalPageHeader'
import { PortalEmptyState } from './ui/PortalEmptyState'

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; icon: React.ElementType }> = {
  active:    { label: 'Active',    bg: 'bg-success/15', text: 'text-success', icon: CheckCircle },
  grace:     { label: 'Grace',     bg: 'bg-warning/15', text: 'text-warning', icon: Clock },
  suspended: { label: 'Suspended', bg: 'bg-destructive/12', text: 'text-destructive', icon: AlertTriangle },
  pending:   { label: 'Pending',   bg: 'bg-primary/8', text: 'text-primary', icon: Clock },
  expired:   { label: 'Expired',   bg: 'bg-muted',     text: 'text-muted-foreground', icon: AlertTriangle },
}

export function PortalAssociationView() {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [applyTarget, setApplyTarget] = useState<any | null>(null)
  const [applyForm, setApplyForm] = useState({ news_org: '', country_code: '', chapter_id: '' })
  const [printMember, setPrintMember] = useState<any | null>(null)

  const { data: scoreData } = useQuery({
    queryKey: ['portal-my-coverage-score'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('journalist_coverage_scores' as any)
        .select(`
          score_total, badge_tier, last_computed_at,
          score_volume, score_reach, score_engagement,
          score_breadth, score_recency, score_format_div, score_accreditation
        `)
        .eq('journalist_id', user.id)
        .maybeSingle() as any)
      return data
    },
  })

  const { data: myMemberships } = useQuery({
    queryKey: ['my-association-memberships'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return []
      const { data } = await (supabase
        .from('association_members' as any)
        .select(`
          *,
          associations(name, region, dues_amount, dues_currency, dues_period, grace_period_days),
          association_chapters(country_name)
        `)
        .eq('journalist_id', user.id)
        .order('applied_at', { ascending: false }) as any)
      return (data || []) as any[]
    },
  })

  const { data: myLeadershipPositions } = useQuery({
    queryKey: ['portal-my-leadership'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return []
      const { data } = await (supabase
        .from('association_leadership' as any)
        .select(`
          id, tenure_start, association_id, member_id,
          association_positions(title, permissions),
          associations(name)
        `)
        .eq('user_id', user.id)
        .eq('is_current', true) as any)
      return (data || []) as any[]
    },
  })

  const { data: applyCurrencies } = useQuery({
    queryKey: ['assoc-currencies-apply', applyTarget?.id],
    enabled: !!applyTarget?.id,
    queryFn: async () => {
      const { data } = await (supabase
        .from('association_currencies' as any)
        .select('currency_code, currency_name, exchange_rate_to_primary, is_primary, is_active')
        .eq('association_id', applyTarget.id)
        .eq('is_active', true)
        .order('is_primary', { ascending: false }) as any)
      return (data || []) as any[]
    },
  })

  const { data: allAssociations } = useQuery({
    queryKey: ['all-associations-portal'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('associations' as any)
        .select('id, name, region, headquarter_country, dues_amount, dues_currency, dues_period, description')
        .eq('status', 'active')
        .order('name') as any)
      return (data || []) as any[]
    },
  })

  const { data: applyChapters } = useQuery({
    queryKey: ['assoc-chapters-apply', applyTarget?.id],
    enabled: !!applyTarget?.id,
    queryFn: async () => {
      const { data } = await (supabase
        .from('association_chapters' as any)
        .select('id, country_code, country_name')
        .eq('association_id', applyTarget.id)
        .order('country_name') as any)
      return (data || []) as any[]
    },
  })

  const myAssocIds = new Set((myMemberships || []).map((m: any) => m.association_id))

  const applyMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')
      const { error } = await (supabase
        .from('association_members' as any)
        .insert({
          association_id: applyTarget.id,
          journalist_id: user.id,
          chapter_id: applyForm.chapter_id || null,
          news_org: applyForm.news_org || null,
          country_code: applyForm.country_code || null,
          status: 'pending',
          dues_status: 'unpaid',
        }) as any)
      if (error) throw error
    },
    onSuccess: () => {
      toast({ title: `Application submitted to ${applyTarget.name}.`, description: 'You will be notified once approved.' })
      qc.invalidateQueries({ queryKey: ['my-association-memberships'] })
      setApplyTarget(null)
      setApplyForm({ news_org: '', country_code: '', chapter_id: '' })
    },
    onError: (e: any) => {
      if (e.message?.includes('unique')) {
        toast({ title: 'Already applied.', description: 'You have an existing application for this association.', variant: 'destructive' })
      } else {
        toast({ title: 'Application failed.', description: e.message, variant: 'destructive' })
      }
    },
  })

  const handlePrint = (m: any) => {
    setPrintMember(m)
    setTimeout(() => {
      window.print()
      setPrintMember(null)
    }, 300)
  }

  return (
    <div className="space-y-6 pb-8">
      <PortalPageHeader
        title="Association Memberships"
        description="Your journalist association memberships and dues status."
      />

      {/* CONORBIT score card */}
      {scoreData ? (
        <div className="border border-border rounded-xl p-5 bg-background">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                CONORBIT Score
              </div>
              <div className="text-[32px] font-bold text-foreground leading-none mt-1">
                {Math.round(scoreData.score_total || 0)}
                <span className="text-[14px] font-normal text-muted-foreground ml-1">/ 1000</span>
              </div>
            </div>
            <div className={`px-3 py-1.5 rounded-xl text-[11px] font-bold uppercase tracking-wide ${
              scoreData.badge_tier === 'gold'
                ? 'bg-accent/20 text-accent'
                : scoreData.badge_tier === 'silver'
                ? 'bg-muted text-muted-foreground'
                : 'bg-[hsl(var(--warning)/0.18)] text-warning'
            }`}>
              {scoreData.badge_tier || 'bronze'} member
            </div>
          </div>

          <div className="h-2 bg-muted rounded-full overflow-hidden mb-4">
            <div
              className="h-full bg-success rounded-full transition-all"
              style={{ width: `${Math.min((scoreData.score_total / 1000) * 100, 100)}%` }}
            />
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {[
              { label: 'Volume',     val: scoreData.score_volume,     max: 150 },
              { label: 'Reach',      val: scoreData.score_reach,      max: 250 },
              { label: 'Engagement', val: scoreData.score_engagement, max: 200 },
              { label: 'Breadth',    val: scoreData.score_breadth,    max: 150 },
              { label: 'Recency',    val: scoreData.score_recency,    max: 100 },
              { label: 'Formats',    val: scoreData.score_format_div, max: 50  },
            ].map(({ label, val, max }) => (
              <div key={label} className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground w-16 flex-shrink-0">{label}</span>
                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-success/60 rounded-full"
                    style={{ width: `${Math.min(((val || 0) / max) * 100, 100)}%` }}
                  />
                </div>
                <span className="text-[11px] font-medium w-7 text-right tabular-nums">{Math.round(val || 0)}</span>
              </div>
            ))}
          </div>

          {scoreData.last_computed_at && (
            <p className="text-justify text-[10.5px] text-muted-foreground mt-3">
              Last computed: {new Date(scoreData.last_computed_at).toLocaleDateString('en', {
                day: 'numeric', month: 'short', year: 'numeric',
              })}
            </p>
          )}
        </div>
      ) : (
        <div className="bg-muted/40 rounded-xl p-4 text-[12.5px] text-muted-foreground text-center">
          Your CONORBIT score will appear here once you submit your first coverage article.
        </div>
      )}

      {/* My memberships */}
      {(myMemberships || []).length > 0 && (
        <div className="space-y-3">
          {(myMemberships || []).map((m: any) => {
            const cfg = STATUS_CONFIG[m.status] ?? STATUS_CONFIG.pending
            const Icon = cfg.icon
            const paidUntil = m.dues_paid_until ? new Date(m.dues_paid_until) : null
            const daysUntil = paidUntil ? Math.ceil((paidUntil.getTime() - Date.now()) / 86400000) : null

            return (
              <div
                key={m.id}
                className={`border rounded-xl p-5 ${
                  m.status === 'suspended'
                    ? 'border-red-200 bg-destructive/6'
                    : m.status === 'grace'
                    ? 'border-yellow-200 bg-warning/8'
                    : 'border-border bg-background'
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold">{m.associations?.name}</div>
                    <div className="text-[11.5px] text-muted-foreground mt-0.5">
                      {m.associations?.region}
                      {m.association_chapters?.country_name ? ` · ${m.association_chapters.country_name} Chapter` : ''}
                    </div>
                  </div>
                  <div className={`flex items-center gap-1 text-[10.5px] font-semibold px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.text}`}>
                    <Icon size={11} />
                    {cfg.label}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11.5px]">
                  <div>
                    <div className="text-muted-foreground mb-0.5">Dues</div>
                    <div className="font-medium">
                      {m.dues_status === 'paid'
                        ? <span className="text-success">Paid</span>
                        : m.dues_status === 'exempted'
                        ? <span className="text-warning">Exempted</span>
                        : <span className="text-destructive">Unpaid</span>}
                    </div>
                  </div>
                  {m.dues_paid_until && (
                    <div>
                      <div className="text-muted-foreground mb-0.5">Valid Until</div>
                      <div className={`font-medium ${daysUntil !== null && daysUntil < 30 ? 'text-warning' : ''}`}>
                        {new Date(m.dues_paid_until).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                  )}
                  {m.membership_number && (
                    <div>
                      <div className="text-muted-foreground mb-0.5">Member No.</div>
                      <div className="font-medium font-mono">{m.membership_number}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-muted-foreground mb-0.5">Dues Rate</div>
                    <div className="font-medium">
                      {m.associations?.dues_currency} {parseFloat(m.associations?.dues_amount ?? 0).toFixed(0)} / {m.associations?.dues_period}
                    </div>
                  </div>
                </div>

                {/* Suspended warning */}
                {m.status === 'suspended' && (
                  <div className="mt-3 flex items-start gap-2 text-[11.5px] text-destructive bg-destructive/12 rounded-lg p-3">
                    <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold mb-0.5">Membership Suspended</div>
                      <div>{m.suspension_reason ?? 'Contact the association to resolve your dues and reinstate membership.'}</div>
                    </div>
                  </div>
                )}

                {/* Grace warning */}
                {m.status === 'grace' && daysUntil !== null && (
                  <div className="mt-3 flex items-start gap-2 text-[11.5px] text-warning bg-warning/15 rounded-lg p-3">
                    <Clock size={13} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold mb-0.5">Grace Period Active</div>
                      <div>Your dues expired. You have {daysUntil > 0 ? `${daysUntil} days` : 'today'} before suspension. Contact the association to pay.</div>
                    </div>
                  </div>
                )}

                {/* Pending note */}
                {m.status === 'pending' && (
                  <div className="mt-3 flex items-start gap-2 text-[11.5px] text-primary bg-primary/8 rounded-lg p-3">
                    <Clock size={13} className="mt-0.5 flex-shrink-0" />
                    <div>Application under review. You will be notified once approved.</div>
                  </div>
                )}

                {/* Print certificate */}
                {m.status === 'active' && m.dues_status !== 'unpaid' && (
                  <button
                    onClick={() => handlePrint(m)}
                    className="mt-3 flex items-center gap-1.5 text-[11.5px] text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Printer size={12} />
                    Download / Print Certificate
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Officer panels, shown when user holds a leadership position */}
      {(myLeadershipPositions || []).length > 0 && (
        <div className="space-y-4">
          <div>
            <h3 className="text-[13px] font-semibold text-foreground">Your Leadership Roles</h3>
            <p className="text-justify text-[11.5px] text-muted-foreground mt-0.5">Manage your association responsibilities.</p>
          </div>
          {(myLeadershipPositions || []).map((pos: any) => (
            <AssociationOfficerPanel
              key={pos.id}
              associationId={pos.association_id}
              associationName={pos.associations?.name ?? ''}
              memberId={pos.member_id}
              positionTitle={pos.association_positions?.title ?? ''}
              tenureStart={pos.tenure_start}
              permissions={pos.association_positions?.permissions ?? {}}
            />
          ))}
        </div>
      )}

      {/* Available associations */}
      <div>
        <h3 className="text-[13px] font-semibold mb-3">Available Associations</h3>
        <div className="space-y-2">
          {(allAssociations || [])
            .filter((a: any) => !myAssocIds.has(a.id))
            .map((a: any) => (
              <div key={a.id} className="flex items-center justify-between gap-3 bg-background border border-border rounded-xl p-4">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium">{a.name}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {a.region}
                    {a.dues_amount > 0 ? ` · ${a.dues_currency} ${parseFloat(a.dues_amount).toFixed(0)} / ${a.dues_period}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setApplyTarget(a)
                    setApplyForm({ news_org: '', country_code: '', chapter_id: '' })
                  }}
                  className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 bg-success/15 text-success rounded-lg font-medium hover:bg-success hover:text-success-foreground transition-colors flex-shrink-0"
                >
                  <Plus size={12} /> Apply
                </button>
              </div>
            ))}

          {(allAssociations || []).filter((a: any) => !myAssocIds.has(a.id)).length === 0 && (myMemberships || []).length > 0 && (
            <div className="text-center py-6 text-[13px] text-muted-foreground">
              You are a member of all available associations.
            </div>
          )}

          {(allAssociations || []).length === 0 && (
            <PortalEmptyState icon={Network} message="No associations available yet." />
          )}
        </div>
      </div>

      {/* Apply sheet */}
      <Sheet open={!!applyTarget} onOpenChange={o => !o && setApplyTarget(null)}>
        <SheetContent side="right" className="w-full max-w-md overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-[15px]">Apply to {applyTarget?.name}</SheetTitle>
          </SheetHeader>
          {applyTarget?.description && (
            <p className="text-justify text-[12.5px] text-muted-foreground mb-4">{applyTarget.description}</p>
          )}

          <div className="space-y-4">
            <div className="bg-muted/30 rounded-xl p-3 space-y-1 text-[12px]">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Dues</span>
                <span className="font-medium">{applyTarget?.dues_currency} {parseFloat(applyTarget?.dues_amount ?? 0).toFixed(2)} / {applyTarget?.dues_period}</span>
              </div>
              {(applyCurrencies || []).filter((c: any) => !c.is_primary).map((c: any) => {
                const eq = parseFloat(applyTarget?.dues_amount ?? 0) * parseFloat(c.exchange_rate_to_primary ?? 1)
                return (
                  <div key={c.currency_code} className="flex justify-between text-muted-foreground">
                    <span>≈ in {c.currency_code}</span>
                    <span>{c.currency_code} {eq.toLocaleString('en', { maximumFractionDigits: 0 })} / {applyTarget?.dues_period}</span>
                  </div>
                )
              })}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Region</span>
                <span className="font-medium">{applyTarget?.region ?? ''}</span>
              </div>
            </div>

            <div>
              <label className="text-[12px] font-medium text-muted-foreground block mb-1">News Organisation</label>
              <input
                type="text"
                value={applyForm.news_org}
                onChange={e => setApplyForm(f => ({ ...f, news_org: e.target.value }))}
                placeholder="Your current employer or media house"
                className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background focus:outline-none focus:border-success"
              />
            </div>

            {(applyChapters || []).length > 0 && (
              <div>
                <label className="text-[12px] font-medium text-muted-foreground block mb-1">Country Chapter</label>
                <select
                  value={applyForm.chapter_id}
                  onChange={e => {
                    const ch = (applyChapters || []).find((c: any) => c.id === e.target.value) as any
                    setApplyForm(f => ({ ...f, chapter_id: e.target.value, country_code: ch?.country_code ?? '' }))
                  }}
                  className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background"
                >
                  <option value="">Select your country chapter</option>
                  {(applyChapters || []).map((ch: any) => (
                    <option key={ch.id} value={ch.id}>{ch.country_name}</option>
                  ))}
                </select>
              </div>
            )}

            <p className="text-[11.5px] text-muted-foreground text-justify">
              Your application will be reviewed by the association administrator. Dues are payable upon approval
              and must be current to maintain active status and full platform benefits.
            </p>

            <button
              disabled={applyMutation.isPending}
              onClick={() => applyMutation.mutate()}
              className="w-full py-2.5 bg-success text-success-foreground rounded-lg text-[13px] font-medium hover:bg-success disabled:opacity-50 transition-colors"
            >
              {applyMutation.isPending ? 'Submitting…' : 'Submit Application'}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Print certificate, hidden from normal view, shown for printing */}
      {printMember && (
        <div id="print-cert" className="hidden print:block fixed inset-0 bg-white p-12 z-[999]">
          <div className="border-4 border-[hsl(var(--foreground))] rounded-2xl p-10 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="text-accent font-bold text-xl tracking-widest uppercase mb-1">CONORBIT</div>
              <div className="text-foreground text-[11px] uppercase tracking-widest mb-8">Platform for Journalism Accreditation</div>
              <div className="text-[13px] text-muted-foreground mb-1">This certifies that</div>
              <div className="text-[22px] font-bold text-foreground mb-1">
                {printMember.journalist_profiles?.full_name ?? 'Member'}
              </div>
              <div className="text-[13px] text-muted-foreground mb-6">is an active member of</div>
              <div className="text-[20px] font-bold text-success mb-1">
                {printMember.associations?.name}
              </div>
              {printMember.association_chapters?.country_name && (
                <div className="text-[13px] text-muted-foreground mb-6">
                  {printMember.association_chapters.country_name} Chapter
                </div>
              )}
              {printMember.membership_number && (
                <div className="text-[11px] text-muted-foreground mb-2">
                  Membership No: <span className="font-mono font-semibold">{printMember.membership_number}</span>
                </div>
              )}
              {printMember.dues_paid_until && (
                <div className="text-[11px] text-muted-foreground mb-8">
                  Valid until: {new Date(printMember.dues_paid_until).toLocaleDateString('en', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
              )}
              <div className="text-[10px] text-muted-foreground mt-8 border-t pt-4">
                Issued by CONORBIT Platform · conorbit.com · {new Date().toLocaleDateString('en', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
